import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Upload, Trash2, Loader2, Plus, ImageOff } from "lucide-react";

export default function ServicesManager() {
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const reload = async () => {
    const { data } = await api.get("/services");
    setItems(data);
  };

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get("/services");
      setItems(data);
    };
    load();
  }, []);

  const patchItem = (id, patch) => {
    setItems((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const handleSave = async (item) => {
    setSavingId(item.id);
    try {
      const bullets = (item.bullets_text || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const payload = { title: item.title, text: item.text, bullets, sort_order: item.sort_order };
      await api.put(`/admin/services/${item.id}`, payload);
      toast.success("Leistung gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSavingId(null); }
  };

  const handleUpload = async (item, file) => {
    if (!file) return;
    setUploadingId(item.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/services/${item.id}`, { image_path: up.path });
      patchItem(item.id, { image_path: up.path });
      toast.success("Bild hochgeladen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setUploadingId(null); }
  };

  const handleRemoveImage = async (item) => {
    if (!window.confirm("Bild entfernen?")) return;
    try {
      await api.put(`/admin/services/${item.id}`, { image_path: "" });
      patchItem(item.id, { image_path: "" });
      toast.success("Bild entfernt");
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Leistung \u201E${item.title}\u201C wirklich löschen?`)) return;
    await api.delete(`/admin/services/${item.id}`);
    toast.success("Gelöscht");
    reload();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/services", { title: newTitle, sort_order: items.length });
      setNewTitle("");
      reload();
      toast.success("Leistung hinzugefügt");
    } catch (err) {
      toast.error("Fehler beim Anlegen");
    } finally { setCreating(false); }
  };

  return (
    <div data-testid="services-manager" className="space-y-6">
      <div className="bg-sand-200 border border-sand-300 p-5 rounded-sm flex flex-col sm:flex-row gap-3 sm:items-center">
        <p className="text-sm text-stone-700 flex-1">
          <strong>Hinweis:</strong> Diese Leistungen erscheinen als Bildkacheln im Design &bdquo;Bold Studio&ldquo; und als abwechselnde Bild-Text-Reihen im Design &bdquo;Werkstatt · Klassisch&ldquo;. Im Editorial-Design werden sie als Icon-Karten gezeigt (ohne Bilder).
        </p>
      </div>

      {/* Add new */}
      <form onSubmit={handleCreate} className="bg-white border border-sand-300 p-5 rounded-sm flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <Label className="mb-2 block">Neue Leistung</Label>
          <Input placeholder="z.B. Holzlackierung" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="service-new-title" />
        </div>
        <Button type="submit" disabled={creating || !newTitle.trim()} data-testid="service-create" className="bg-stone-900 hover:bg-clay text-sand-100 rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Hinzufügen
        </Button>
      </form>

      {/* List */}
      <div className="space-y-4">
        {items.map((item) => (
          <article key={item.id} data-testid={`service-card-${item.id}`} className="bg-white border border-sand-300 rounded-sm p-5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Image side */}
              <div className="lg:col-span-4">
                <div className="aspect-[4/3] border border-sand-300 rounded-sm bg-sand-100 flex items-center justify-center overflow-hidden">
                  {item.image_path ? (
                    <img src={fileUrl(item.image_path)} alt={item.title} className="w-full h-full object-cover" data-testid={`service-image-${item.id}`} />
                  ) : (
                    <div className="text-center px-4">
                      <ImageOff className="w-7 h-7 mx-auto text-stone-600 mb-2" />
                      <p className="text-xs text-stone-600">Kein Bild gesetzt — es wird ein Standard-Stock-Foto angezeigt.</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 mt-3">
                  <label className="inline-block">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      onChange={(e) => { handleUpload(item, e.target.files?.[0]); e.target.value = ""; }}
                      className="hidden"
                      data-testid={`service-upload-${item.id}`}
                    />
                    <span className="inline-flex w-full items-center justify-center gap-2 px-4 py-2 bg-stone-900 hover:bg-clay text-sand-100 rounded-sm cursor-pointer transition-colors text-sm">
                      {uploadingId === item.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</> : <><Upload className="w-4 h-4" /> Bild hochladen (JPG / PNG)</>}
                    </span>
                  </label>
                  {item.image_path && (
                    <Button variant="outline" size="sm" onClick={() => handleRemoveImage(item)} data-testid={`service-image-remove-${item.id}`} className="border-sand-300 text-stone-700">
                      <Trash2 className="w-4 h-4 mr-1" /> Bild entfernen
                    </Button>
                  )}
                </div>
              </div>

              {/* Text side */}
              <div className="lg:col-span-8 space-y-3">
                <div>
                  <Label className="mb-1.5 block text-sm">Titel</Label>
                  <Input value={item.title || ""} onChange={(e) => patchItem(item.id, { title: e.target.value })} data-testid={`service-title-${item.id}`} />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Beschreibung</Label>
                  <Textarea rows={3} value={item.text || ""} onChange={(e) => patchItem(item.id, { text: e.target.value })} data-testid={`service-text-${item.id}`} />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Stichpunkte <span className="text-stone-600 font-normal">(eine Zeile pro Punkt — werden im Werkstatt-Design angezeigt)</span></Label>
                  <Textarea
                    rows={3}
                    value={item.bullets_text ?? (item.bullets || []).join("\n")}
                    onChange={(e) => patchItem(item.id, { bullets_text: e.target.value })}
                    placeholder={"z.B.\nWände & Decken\nHeizkörper- & Türlackierung"}
                    data-testid={`service-bullets-${item.id}`}
                  />
                </div>
                <div className="flex flex-wrap gap-3 items-center pt-2">
                  <Button onClick={() => handleSave(item)} disabled={savingId === item.id} data-testid={`service-save-${item.id}`} className="bg-stone-900 hover:bg-clay text-sand-100 rounded-sm">
                    {savingId === item.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichert…</> : <><Save className="w-4 h-4 mr-2" /> Speichern</>}
                  </Button>
                  <Button variant="ghost" onClick={() => handleDelete(item)} data-testid={`service-delete-${item.id}`} className="text-stone-700 hover:text-clay hover:bg-sand-200 ml-auto">
                    <Trash2 className="w-4 h-4 mr-1" /> Löschen
                  </Button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
